import React, { useState, useEffect } from 'react'
import { Modal, Button, Grid, Box, withTheme, ThemeProvider } from '@deseretbook/react-ui'
import { usePopup, useMediaQuery } from '@deseretbook/react-utils'
import PropTypes from 'prop-types'
import qs from 'qs'
import { SearchBox, Hits, Pagination, SidebarContainer, AlgoliaContainer } from './'
import { Analytics } from '../../../dbcom'

/**
 * Search Component using Algolia
 *
 * @see https://www.algolia.com/doc/api-reference/widgets/search-box/react/
 *
 * @param {object} theme - The react theme used for getting the primary color
 *
 * @returns {function} React component
 */
const ModalSearch = ({ theme }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [currentQuery, setCurrentQuery] = useState('')
  const searchModal = usePopup()

  // For when the content reaches its mobile breakpoint
  const isMobileOrSmaller = useMediaQuery({ maxWidth: 500 })
  const isTabletOrSmaller = useMediaQuery({ maxWidth: 768 })
  const isMediumOrSmaller = useMediaQuery({ maxWidth: 1015 })

  const searchPlaceholder = isMobileOrSmaller ? 'Search' : 'What can we help you find?'

  /**
   * Handles tracking the opening of the search modal
   */
  const handleOpen = () => {
    Analytics.event(
      {
        category: 'Algolia Search Container',
        action: 'User opened the Algolia search modal',
      },
      { isInteractive: true },
    )

    setIsOpen(true)
  }

  /**
   * Handles tracking the closing of the search modal
   */
  const handleClose = () => {
    Analytics.event(
      {
        category: 'Algolia Search Container',
        action: 'User closed the Algolia search modal',
      },
      { isInteractive: true },
    )

    setIsOpen(false)
  }

  const gridColumns = [20, Grid.fr(3)]

  if (isMediumOrSmaller) {
    gridColumns.shift()
  }

  const secondaryThemeColor =
    (theme && theme.palette && theme.palette.secondary && theme.palette.secondary.main) ||
    'rgb(137, 149, 93)'

  useEffect(() => {
    const { query = '' } = qs.parse(location.search.slice(1))
    const newQuery = decodeURIComponent(query)

    if (currentQuery !== newQuery) {
      setCurrentQuery(newQuery)
    }
  }, [location.search])

  return (
    <ThemeProvider>
      <AlgoliaContainer isRoot>
        <Box isFullwidth>
          <Button
            {...searchModal.triggerProps}
            type="button"
            title="Search"
            isFullwidth
            icon="search"
            aria-label="search"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItem: 'center',
              borderRadius: '2rem',
            }}
            onClick={handleOpen}
          >
            {currentQuery || searchPlaceholder}
          </Button>
          <Modal
            {...searchModal.popupProps}
            close={handleClose}
            isOpen={isOpen}
            size="90%"
            alignment="top"
          >
            <Modal.Header style={{ backgroundColor: secondaryThemeColor }}>
              <SearchBox />
            </Modal.Header>
            <Modal.Content id="algolia-container" style={{ scrollBehavior: 'smooth' }}>
              <Grid columns={gridColumns} gap={isTabletOrSmaller ? 2 : 6}>
                <Grid.Item>
                  <SidebarContainer />
                </Grid.Item>
                <Grid.Item>
                  <Hits isRoot />
                  <Pagination />
                </Grid.Item>
              </Grid>
            </Modal.Content>
          </Modal>
        </Box>
      </AlgoliaContainer>
    </ThemeProvider>
  )
}

ModalSearch.propTypes = {
  theme: PropTypes.shape({
    palette: PropTypes.shape({
      secondary: PropTypes.shape({
        light: PropTypes.string,
        main: PropTypes.string,
        dark: PropTypes.string,
      }),
    }),
  }),
}

const ModalSearchWithTheme = withTheme(ModalSearch)

/**
 * Wraps the modal search with a theme provider.
 * This allows us to access the theme within modal search and
 * apply the primary color to the background
 *
 * @returns {function} React Component
 */
const ModalSearchWrapper = () => (
  <ThemeProvider>
    <ModalSearchWithTheme />
  </ThemeProvider>
)

export default ModalSearchWrapper
